import React from 'react';
import Image from 'next/image';
import DevicePartnerBadge from '@/components/ui/DevicePartnerBadge/DevicePartnerBadge';
import type { LanguageType } from '@/lib/types';

interface DeviceTestimonialCardProps {
  imageUrl: string;
  imageAlt: string;
  /** Device name — passed to <DevicePartnerBadge> as `title`. */
  title: string;
  description: string;
  /** Drives the LTR/RTL padding swap on the text block. */
  language: LanguageType;
  /**
   * `<Image sizes>` hint. Differs between desktop grid (~25vw) and mobile
   * swiper (50vw on tablet, 100vw on phone), so the caller decides.
   */
  imageSizes: string;
  /** Badge sizing — the caller resolves `isLg`/`isTb` and passes the chosen variant. */
  badgeSize: 'desktop' | 'mobile';
}

/**
 * Single card for LPDevicesSection — used in both the desktop grid and the
 * mobile swiper. Extracted so the section file stays focused on layout
 * orchestration (grid vs swiper) rather than per-card markup.
 *
 * The `device-image-card` className on the image is intentional: the section
 * queries it with `document.querySelectorAll('.device-image-card')` to wire
 * the GSAP parallax animation. Do not rename without updating that selector.
 *
 * Note on the image wrapper edge: this uses `outline-*` (paints outside the
 * box, doesn't get clipped by `overflow-hidden`), unifying what previously
 * drifted between the desktop and mobile variants (the mobile one used
 * `border-*` — visually similar but layout-distinct).
 */
const DeviceTestimonialCard: React.FC<DeviceTestimonialCardProps> = ({
  imageUrl,
  imageAlt,
  title,
  description,
  language,
  imageSizes,
  badgeSize,
}) => {
  return (
    <div className="relative w-full">
      <div className="flex flex-col gap-[2rem] lg:gap-[2.5rem]">
        <div className="
          border-radius-main
          outline-secondary outline-stroke-soft-200
          overflow-hidden
          relative
          ">
          <Image
            src={imageUrl}
            alt={imageAlt}
            width={800}
            height={800}
            sizes={imageSizes}
            loading="lazy"
            style={{
              scale: 1.1,
              width: '100%',
              height: 'auto',
              aspectRatio: '4 / 4',
              objectFit: 'cover',
            }}
            className="device-image-card"
          />
        </div>
        <div
          className={`
            flex flex-col gap-[1.25rem] lg:gap-[2rem]
            ${language === 'english' ?
              `
              pl-[0.5rem] pr-[0.75rem]
              lg:pl-[0.75rem] lg:pr-[2rem]
              `
              :
              `
              pr-[0.5rem] pl-[0.75rem]
              lg:pr-[0.75rem] lg:pl-[2rem]
              `
            }
          `}>
          <div className="flex flex-row lg:flex-col justify-between lg:items-start lg:gap-[1.25rem]">
            <div className="w-fit order-2 lg:order-1">
              <DevicePartnerBadge title={title} size={badgeSize} disableBg={true} darkMode={true} />
            </div>
          </div>

          <p className="
            text-text-primary
            text-20-fixed--neue-june
            ">
            {description}
          </p>
        </div>
      </div>
    </div>
  );
};

export default DeviceTestimonialCard;
